import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnInit,
  signal,
  untracked
} from '@angular/core';
import {
  CardContainerComponent,
  ChevronPosition,
  closePopupData,
  createErrorPopupData,
  InfobubbleComponent,
  KeyValueLabel,
  LoaderComponent,
  MobilePhoneSignalInputComponent,
  SelectSignalComponent,
  StatusMessagePopupV2Component,
  StatusPopupElement,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface,
  YesNoSignalInputComponent
} from '@tribeauto/ngx-mobilite-material';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { UserService } from '../../user-connection/user.service';
import { disabled, form, FormField } from '@angular/forms/signals';
import { NotificationForm, TranslationForm } from './parameters.model';
import { ApplicationStore } from '../../../core/services/application-store.service';
import { PreferencesService } from '../preferences/preferences.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { EventType, UserNotificationsPreferences } from '../preferences/preferences.model';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../helpers/popup.helper';

@Component({
  selector: 'pvmo-parameters',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    CardContainerComponent,
    YesNoSignalInputComponent,
    FormField,
    MobilePhoneSignalInputComponent,
    SelectSignalComponent,
    LoaderComponent,
    StatusMessagePopupV2Component,
    InfobubbleComponent
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './parameters.component.html',
  styleUrl: './parameters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParametersComponent implements OnInit {
  private readonly translationService = inject(TranslationService);
  private readonly translateService = inject(TranslateService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly userService = inject(UserService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly cdr = inject(ChangeDetectorRef);

  translations: KeyValueLabel[] = [
    { key: 'b2b.fr', value: 'Français' },
    { key: 'b2b.en', value: 'English' }
  ];
  userData = this.applicationStore.instant('userData')();
  statusPopupElement: StatusPopupElement = closePopupData(
    createErrorPopupData(
      this.translateService.instant('application.parameters.notification.error.title'),
      this.translateService.instant('application.parameters.notification.error.message'),
      this.translateService.instant('application.parameters.notification.error.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    )
  );
  isUserConnected = this.userService.isUserConnected;
  lastValueActiveSms: boolean | null = null;
  isLoadingPreferences = signal<boolean>(true);

  readonly translationModel = signal<TranslationForm>({
    choice:
      this.translations.find(translation => translation.key === 'b2b.' + localStorage.getItem('lang')) ??
      this.translations[0]
  });
  readonly notificationModel = signal<NotificationForm>({
    isActiveSmsNotification: null,
    mobile: this.userData?.mobilePhone ?? ''
  });
  readonly translationForm = form(this.translationModel);
  readonly notificationForm = form(this.notificationModel, path => {
    disabled(path.isActiveSmsNotification, { when: () => !this.userData?.mobilePhone });
    disabled(path.mobile);
  });

  protected readonly chevronPosition = ChevronPosition;

  constructor() {
    effect(() => {
      const choiceValue = this.translationForm.choice().value();
      untracked(() => {
        const lang = choiceValue?.key === 'b2b.en' ? 'en' : 'fr';
        this.translationService.selectedFile.update(() => (choiceValue ? choiceValue.key : 'b2b.fr'));
        this.translateService.use(lang);
        localStorage.setItem('lang', lang);
      });
    });
    effect(() => {
      const isActiveSmsNotificationValue = this.notificationForm.isActiveSmsNotification().value();
      untracked(() => {
        if (isActiveSmsNotificationValue !== this.lastValueActiveSms) {
          this.updateStatusChangeSmsNotifications(isActiveSmsNotificationValue);
        }
      });
    });
  }

  ngOnInit(): void {
    this.preferencesService
      .getUserNotificationsPreferencesList()
      .pipe(
        tap(() => this.isLoadingPreferences.set(true)),
        catchError(() => {
          this.setErrorStatusPopup();
          return of(null);
        }),
        tap(userNotificationsPreferencesList => {
          const userNotificationsPreferencesStatusChange = userNotificationsPreferencesList?.find(
            userNotificationsPreferences => EventType.STATUS_CHANGE === userNotificationsPreferences.eventType
          );
          this.notificationForm
            .isActiveSmsNotification()
            .value.set(this.userData?.mobilePhone ? (userNotificationsPreferencesStatusChange?.sms ?? true) : false);
          if (userNotificationsPreferencesStatusChange) {
            this.applicationStore.update(
              'notificationsStatusChangePreferences',
              userNotificationsPreferencesStatusChange
            );
          }
        }),
        finalize(() => {
          this.isLoadingPreferences.set(false);
        })
      )
      .subscribe();
  }

  closeStatusMessagePopup(): void {
    this.statusPopupElement = closePopupData(this.statusPopupElement);
  }

  private updateStatusChangeSmsNotifications(isActiveSmsNotificationValue: boolean | null): void {
    const userNotificationsPreferences = this.applicationStore.instant('notificationsStatusChangePreferences')();
    const userNotificationsPreferencesToSave: UserNotificationsPreferences = userNotificationsPreferences
      ? {
          ...(userNotificationsPreferences as UserNotificationsPreferences),
          sms: isActiveSmsNotificationValue ?? false
        }
      : {
          eventType: EventType.STATUS_CHANGE,
          sms: isActiveSmsNotificationValue ?? false,
          email: false,
          notification: false
        };
    this.preferencesService.updateUserNotificationsPreferences(userNotificationsPreferencesToSave).subscribe({
      next: () => {
        this.lastValueActiveSms = isActiveSmsNotificationValue ?? false;
      },
      error: () => {
        this.notificationForm.isActiveSmsNotification().value.set(this.lastValueActiveSms);
        this.setErrorStatusPopup();
      }
    });
  }

  private setErrorStatusPopup(): void {
    this.statusPopupElement.isVisible = true;
    this.cdr.markForCheck();
  }
}
